import { Pipe, PipeTransform } from '@angular/core';
import { LanguageService } from '../shared/services/language-service';
import { MomentPipe } from './moment';

export const languagesWith12hrFormat = [
  'ar-eg',
  'ar-sa',
  'en-ca',
  'en-gb',
  'en-us',
  'es-co',
  'es-cr',
  'es-hn',
  'es-ni',
  'es-pa',
  'es-sv',
  'ko-kr',
].concat(['fil']);

@Pipe({ name: 'customDatePipe', standalone: true })
export class CustomDatePipe implements PipeTransform {
  constructor(private languageService: LanguageService) {}

  public locale = this.languageService.currentLanguage;

  private languagesWith12HrFormatHandledByMomentDifferently = [
    'ar-eg',
    'ar-sa',
    'en-gb',
    'es-co',
    'es-cr',
    'es-hn',
    'es-ni',
    'es-pa',
    'es-sv',
  ].concat(['fil']);

  private languagesWithSpecificLLDformat = {
    'ar-ae': 'LL dd',
    'da-dk': 'dd [d.] LL',
    'fr-ca': 'dd LL',
    'fr-ch': 'dd LL',
    'fr-fr': 'dd LL',
    'ja-jp': 'LL dd',
    'nb-no': 'dd LL',
    'sv-se': 'dd LL',
    'zh-cn': 'LLdd',
    'zh-hk': 'LLdd',
  };

  private languagesWithSpecificLLLLDformat = {
    'ar-ae': 'dddd LL',
    'da-dk': 'dddd [d.] LL',
    'fr-ca': 'dddd LL',
    'fr-ch': 'dddd LL',
    'fr-fr': 'dddd LL',
    'it-it': 'dddd LL',
    'ja-jp': 'LL dddd',
    'nb-no': 'dddd LL',
    'nl-be': 'dddd LL',
    'nl-nl': 'dddd LL',
    'sv-se': 'dddd LL',
    'zh-cn': 'LLdddd',
    'zh-hk': 'LLdddd',
  };

  // NOTE: timeBracketed only added for lll format
  public transform(date: Date | string, format = 'd MMM yyyy', timeBracketed = false): string {
    if (!date || !this.locale) return '';

    if (this.locale.startsWith('zh')) {
      const fullDate = new Date(date);

      const hours24 = fullDate.getHours();
      const ampm = hours24 >= 12 ? '晚上' : '早上';

      switch (format) {
        case 'A':
          return ampm;
        case 'h:mm A':
          format = 'A h:mm';
          break;
        case 'hh:mm A':
          format = 'A hh:mm';
          break;
        default:
          break;
      }

      if (format === 'LLLL') {
        format = 'llll';
      }
    }

    if (format === 'LLLLD') {
      if (Object.prototype.hasOwnProperty.call(this.languagesWithSpecificLLLLDformat, this.locale)) {
        format = this.languagesWithSpecificLLLLDformat[this.locale];
      } else {
        format = 'dddd, LL';
      }
    }

    if (format === 'LLD') {
      if (Object.keys(this.languagesWithSpecificLLDformat).some((x: string) => x === this.locale)) {
        format = this.languagesWithSpecificLLDformat[this.locale];
      } else {
        format = 'dd, LL';
      }
    }

    if (this.languagesWith12HrFormatHandledByMomentDifferently.some((x: string) => x === this.locale)) {
      if (format === 'LT') {
        format = 'h:mm A';
      }
      if (format === 'LLLL') {
        format = 'dddd, LL h:mm A';
      }
      if (format === 'LLL') {
        format = 'LL h:mm A';
      }
      if (format === 'lll') {
        format = timeBracketed ? 'll (h:mm A)' : 'll h:mm A';
      }
      if (format === '24') {
        format = 'HH:mm';
      }
    }

    if (format === 'lll' && timeBracketed) {
      format = 'll (LT)';
    }

    return new MomentPipe(this.languageService).transform(date, format);
  }
}
